import { useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { AsyncButton } from '@/components/common/AsyncButton';

export type ResolveAction = 'approve' | 'reject';

interface ResolveDialogProps {
  action: ResolveAction;
  // count > 1 renders the bulk wording ("Approve Requests" / "Approve All").
  count: number;
  onClose: () => void;
  // Resolves with the trimmed comment; throws on failure (caller toasts the message).
  onSubmit: (comment: string) => Promise<void>;
}

// Single + bulk approve/reject modal. The proposed change IS the grid row; this is
// purely the mandatory-comment confirmation (legacy ResolveModal / BulkResolveModal).
export function ResolveDialog({ action, count, onClose, onSubmit }: ResolveDialogProps) {
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  const bulk = count > 1;
  const approve = action === 'approve';
  const title = approve
    ? bulk
      ? 'Approve Requests'
      : 'Approve Request'
    : bulk
      ? 'Decline Requests'
      : 'Decline Request';
  const submitLabel = approve ? (bulk ? 'Approve All' : 'Approve') : bulk ? 'Decline All' : 'Decline';

  const invalid = comment.trim().length === 0;

  const handleSubmit = async () => {
    setTouched(true);
    if (invalid) return;
    await onSubmit(comment.trim());
  };

  return (
    <Modal
      show
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-dark btn-sm" onClick={onClose}>
            Cancel
          </button>
          <AsyncButton
            className={`btn btn-sm ${approve ? 'btn-primary' : 'btn-danger'}`}
            onClick={handleSubmit}
          >
            {submitLabel}
          </AsyncButton>
        </>
      }
    >
      {bulk && <p className="mb-2">{count} request(s) selected.</p>}
      <div>
        <label className="form-label">
          Comment <span className="text-danger">*</span>
        </label>
        <textarea
          className={`form-control${touched && invalid ? ' is-invalid' : ''}`}
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={() => setTouched(true)}
        />
        {touched && invalid && <div className="invalid-feedback">A comment is required.</div>}
      </div>
    </Modal>
  );
}
